import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Users, Calendar, MapPin } from "lucide-react";
import { EditorialPage, handwritten } from "../components/editorial";
import StoryPageHero from "../components/story/StoryPageHero";
import Reveal from "../components/story/Reveal";
import Doodle from "../components/story/Doodle";
import Highlighter from "../components/story/Highlighter";
import Button from "../components/Button";
import Navbar from "../components/Navbar";

const seeded = [
  {
    name: "Hackney Run Club",
    emoji: "🏃",
    bg: "var(--color-paper-mint)",
    members: 184,
    area: "Hackney",
    cadence: "Sun 9am · Wed 6:30am",
    blurb:
      "10K loop every Sunday, 5K threshold sessions on Wednesdays. Coffee at the cafe after.",
  },
  {
    name: "London Book Crew",
    emoji: "📚",
    bg: "var(--color-paper-salmon)",
    members: 92,
    area: "Shoreditch",
    cadence: "Last Thu of the month",
    blurb:
      "One book, one pub, no shame in not finishing. Currently reading: Trust, Hernán Díaz.",
  },
  {
    name: "Sunday Picnic Co.",
    emoji: "🧺",
    bg: "var(--color-paper-tan)",
    members: 56,
    area: "Various parks",
    cadence: "Every Sunday",
    blurb:
      "BYO snack + a blanket. London Fields, Victoria Park, Hampstead Heath on rotation.",
  },
  {
    name: "Battersea Volleyball",
    emoji: "🏐",
    bg: "var(--color-paper-mint)",
    members: 124,
    area: "Battersea Park",
    cadence: "Tue & Thu 6:30pm",
    blurb:
      "Mixed-skill grass court volleyball. Two courts, four teams, rotate every game.",
  },
  {
    name: "Borough Pizza Friday",
    emoji: "🍕",
    bg: "var(--color-paper-salmon)",
    members: 38,
    area: "Borough Market",
    cadence: "Every Friday 7pm",
    blurb:
      "End the week with a slice. Padella, Pizza Pilgrims, and the occasional outlier.",
  },
  {
    name: "Regent's Run Club",
    emoji: "🏃",
    bg: "var(--color-paper-tan)",
    members: 71,
    area: "Regent's Park",
    cadence: "Sun 9am",
    blurb:
      "A flat 8K loop around the Outer Circle. Pace groups: chat, threshold, race.",
  },
];

export default function CommunitiesPage() {
  useEffect(() => {
    document.title = "Communities | LetsLoop";
  }, []);

  return (
    <>
      <Navbar />
      <EditorialPage>
        {/* ── Header ────────────────────────────────────────────── */}
        <StoryPageHero
          kicker="communities"
          title={
            <>
              Groups that <Highlighter tone="mint">actually meet.</Highlighter>
            </>
          }
          lede={
            <>
              Real, recurring loops, not dormant group chats. LetsLoop seeds
              each area with reference communities so the feed isn&rsquo;t empty
              on day one.
            </>
          }
          marks={[
            {
              name: "loop",
              size: 66,
              rotate: 10,
              className: "left-[6%] top-[28%] text-teal/35",
            },
            {
              name: "heart",
              size: 34,
              rotate: -12,
              className: "right-[10%] top-[24%] text-salmon-dark/50",
            },
            {
              name: "star",
              size: 40,
              className: "right-[26%] bottom-[14%] text-salmon/55",
            },
          ]}
        />

        {/* ── Seeded communities grid ──────────────────────────── */}
        <section className="relative w-full ground-peach px-6 md:px-12 py-20 md:py-24 overflow-hidden">
          <Doodle
            name="squiggle"
            size={96}
            rotate={-5}
            className="absolute w-8 h-8 md:w-auto md:h-auto right-[4%] max-md:right-[5%] top-[10%] text-salmon/35"
          />
          <div className="max-w-6xl mx-auto relative">
            <div className="flex items-baseline gap-4 mb-10">
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-salmon-dark whitespace-nowrap">
                01: Seeded in London
              </span>
              <span className="flex-1 h-px bg-ink/15" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {seeded.map((c, i) => (
                <Reveal
                  key={c.name}
                  y={30}
                  delay={i * 60}
                  className="bg-canvas border border-hairline rounded-3xl p-6 shadow-[6px_6px_0_0_rgba(5,5,5,0.06)] hover:shadow-[0_18px_44px_-20px_rgba(45,56,73,0.32)] hover:-translate-y-1.5 transition-all duration-300"
                >
                  <div
                    className="aspect-[5/3] rounded-2xl flex items-center justify-center text-6xl mb-5 border border-ink/15"
                    style={{ backgroundColor: c.bg }}
                  >
                    {c.emoji}
                  </div>
                  <h3
                    className="font-headline text-2xl tracking-[-0.02em] text-ink mb-2"
                    style={{ fontWeight: 700 }}
                  >
                    {c.name}
                  </h3>
                  <p className="text-sm text-gray-700 leading-relaxed mb-4">
                    {c.blurb}
                  </p>
                  <div className="space-y-2 text-xs text-gray-500 border-t border-ink/10 pt-4">
                    <div className="flex items-center gap-2">
                      <Users size={12} className="text-salmon" />
                      <span className="font-semibold text-ink">
                        {c.members} members
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin size={12} className="text-salmon" />
                      <span>{c.area}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar size={12} className="text-salmon" />
                      <span>{c.cadence}</span>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ── Start a community CTA ────────────────────────────── */}
        <section className="relative w-full bg-salmon px-6 md:px-12 py-24 md:py-32 overflow-hidden">
          <Doodle
            name="starFilled"
            size={28}
            className="absolute w-8 h-8 md:w-auto md:h-auto left-[8%] top-[16%] text-cream/70"
          />
          <Doodle
            name="burst"
            size={44}
            className="absolute w-8 h-8 md:w-auto md:h-auto right-[9%] bottom-[18%] text-on-salmon/25"
          />
          <Reveal y={28} className="relative z-10">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-4 mb-6">
                <span className="text-xs font-bold uppercase tracking-[0.25em] text-on-salmon/80 whitespace-nowrap">
                  02: Start your own
                </span>
                <span className="flex-1 h-px bg-ink/20" />
              </div>
              <p
                className="text-3xl text-on-salmon/80 -rotate-1 mb-3"
                style={handwritten}
              >
                already running one?
              </p>
              <h2
                className="font-headline text-on-salmon text-4xl md:text-6xl tracking-[-0.03em] mb-6 leading-[1.12]"
                style={{ fontWeight: 700 }}
              >
                Move your group
                <br />
                <span className="italic">somewhere it lives.</span>
              </h2>
              <p className="text-lg text-on-salmon/85 leading-relaxed mb-8 max-w-2xl">
                Run a book club, a Sunday cycle crew, a co-working brunch? Turn
                your WhatsApp into loops. Members get a feed instead of a
                backlog. New people loop in instead of asking to be added. Free
                to join.
              </p>
              <div className="flex flex-col sm:flex-row items-start gap-4">
                <Link to="/how-it-works#communities">
                  <Button variant="primary" size="lg">
                    How communities work
                    <ArrowRight size={16} className="ml-2 inline" />
                  </Button>
                </Link>
                <Link
                  to="/download"
                  className="text-on-salmon/80 font-semibold border-b-2 border-ink/20 hover:border-salmon hover:text-salmon transition-colors text-base inline-flex items-center gap-1.5 self-center"
                >
                  See live loops in the app &rarr;
                </Link>
              </div>
            </div>
          </Reveal>
        </section>
      </EditorialPage>
    </>
  );
}
